import Activity, { type ActivityStatus } from '@rio-cloud/rio-uikit/Activity';
import type { ReactNode } from 'react';

import { NotificationStates, type TransformedData } from '../../../services/types';
import { NoData } from '../asset/NoData';
import { NotificationBadge } from '../asset/NotificationBadge';

export type ClusterAsset = Omit<Partial<TransformedData>, 'activityDuration'> & {
    activityDuration?: string | number;
};

type ClusterAssetHeaderProps = {
    asset: ClusterAsset;
};

const ClusterAssetHeader = ({ asset }: ClusterAssetHeaderProps) => {
    const numberWarnings = asset.notificationWarnings;
    const numberExceptions = asset.notificationExceptions;

    return (
        <div className='display-flex padding-10 gap-5 align-items-center'>
            <div className='text-size-16 margin-top--1'>
                <span className={`uikit-icon icon-${asset.type}`} data-testid='asset-icon' />
            </div>
            <div className='text-size-16 text-medium flex-1-1'>
                <div className='line-height-125rel' data-testid='asset-name'>
                    {asset.name}
                </div>
            </div>
            {numberExceptions && numberExceptions > 0 && (
                <div className='margin-left-4'>
                    <NotificationBadge level={NotificationStates.exception} amount={numberExceptions} />
                </div>
            )}
            {numberWarnings && numberWarnings > 0 && (
                <div className='margin-left-4'>
                    <NotificationBadge level={NotificationStates.warning} amount={numberWarnings} />
                </div>
            )}
            <div className='margin-left-4'>
                {asset.activity && (
                    <Activity
                        bsSize='sm'
                        activity={asset.activity as ActivityStatus}
                        duration={asset.activityDuration}
                        isOutdated={asset.drivingTimeOutdated}
                    />
                )}
            </div>
        </div>
    );
};

type ClusterAssetPropProps = {
    asset: ClusterAsset;
    propName: 'address' | 'driver';
    icon?: string;
};

const ClusterAssetProp = ({ asset, propName, icon = '' }: ClusterAssetPropProps) => {
    const value = asset[propName] as ReactNode;

    return (
        <div className='ClusterAssetProp display-flex padding-x-10 padding-top-5' data-testid={propName}>
            <div className='margin-left-2 margin-right-5'>
                <span className={`text-size-16 text-color-gray uikit-icon ${icon}`} />
            </div>
            <div className='flex-1-1 padding-top-2'>
                <div className='text-color-darker line-height-125rel'>{value ?? <NoData />}</div>
            </div>
        </div>
    );
};

type ClusterSidebarAssetProps = {
    asset: ClusterAsset;
    onClick?: () => void;
};

export const ClusterSidebarAsset = ({ asset, onClick }: ClusterSidebarAssetProps) => {
    const classes = `ClusterSidebarAsset border rounded margin-bottom-10 padding-bottom-10
        hover-bg-lightest cursor-pointer`;

    return (
        <div className={classes} data-testid={asset.vehicleId} onClick={onClick}>
            <ClusterAssetHeader asset={asset} />
            <hr className='margin-0' />
            <ClusterAssetProp asset={asset} propName='address' icon='icon-map-marker' />
            <ClusterAssetProp asset={asset} propName='driver' icon='icon-driver' />
        </div>
    );
};
